import { create, update } from '@/core/repo';
import { store } from '@/core/store';
import type { EntityOf, Kind } from '@/core/types';

type Fields<K extends Kind> = Omit<EntityOf<K>, 'id' | 'kind' | 'createdAt' | 'updatedAt'>;

/**
 * Create or refresh a record with a fixed id (`oura-…`, `renpho-…`, `sc-…`), so syncing or
 * importing twice never duplicates. Only fields that are set and changed are written (no
 * needless sync traffic), and something the user deleted stays deleted.
 * `keep`: fields the user may have changed by hand, never overwritten once set.
 */
export function upsert<K extends Kind>(
  kind: K,
  id: string,
  fields: Fields<K>,
  keep: (keyof Fields<K>)[] = [],
): 'new' | 'changed' | 'same' {
  const cur = store.get(kind, id) as Record<string, unknown> | undefined;
  if (!cur) {
    const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));
    create(kind, { ...clean, id } as never);
    return 'new';
  }
  if (cur.deleted) return 'same';
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined) continue;
    if (keep.includes(k as keyof Fields<K>) && cur[k] !== undefined) continue;
    if (JSON.stringify(cur[k]) !== JSON.stringify(v)) patch[k] = v;
  }
  if (!Object.keys(patch).length) return 'same';
  update(kind, id, patch as never);
  return 'changed';
}
